// listaEsperaHelpers.js
//
// Regla de negocio ÚNICA para decidir si un registro de
// pos_guest_queue (status 1..4, ver fullStatusCatalog en
// VentanaListaEspera.js) es:
//   - una "Reservación" futura (todavía no es parte de la operación
//     del día), o
//   - parte de la "Lista de Espera de Hoy" (operativo: walk-ins +
//     reservaciones cuya fecha ya llegó).
//
// Vive en un solo archivo compartido entre RevenueSelector.js (el
// banner con el conteo) y VentanaListaEspera.js (el modal con las
// pestañas) a propósito: tener la misma regla duplicada en dos
// archivos fue exactamente lo que causó el bug anterior donde un
// registro mostraba "Reserva" en un lado y "Espera" en otro — ahora
// solo hay un lugar que puede desincronizarse consigo mismo.
//
// Definición de "hoy": se compara por día calendario en hora local
// del dispositivo (no UTC), así que una reserva para las 20:00 de hoy
// ya cuenta como operativa desde las 00:00 de ese mismo día — que es
// justo el comportamiento pedido: "cuando llegue la fecha programada
// de una reservación, esta deberá integrarse automáticamente a la
// lista operativa del día".

export function isFutureReservationDate(scheduledTime) {
  if (!scheduledTime) return false;
  const d = new Date(scheduledTime);
  if (isNaN(d.getTime())) return false;

  const now = new Date();
  const dDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  return dDate.getTime() > todayDate.getTime();
}

// Requerimiento: "Hoy" y "Reservaciones" solo deben mostrar registros
// vigentes/pendientes — lo concluido (ya llegó) o vencido (la hora
// programada ya pasó y nadie lo marcó) se consulta desde Historial.
//
// Comparación por fecha+hora EXACTA (no solo el día calendario, a
// diferencia de isFutureReservationDate) — una reserva de hoy a las
// 15:00 sigue vigente en "Hoy" hasta que den las 15:00, no desde las
// 00:00.
export function hasReservationTimePassed(scheduledTime) {
  if (!scheduledTime) return false;
  const d = new Date(scheduledTime);
  if (isNaN(d.getTime())) return false;
  return d.getTime() < Date.now();
}

// Un registro se considera "concluido/vencido" (→ Historial) cuando:
//   - ya llegó (status 2 "Llegó"), sin importar si ya se le asignó
//     mesa desde aquí o no — decisión de negocio explícita: en cuanto
//     cambia a "Llegó" deja de ser un pendiente de Hoy/Reservaciones; o
//   - su fecha/hora programada ya pasó (Reserva/Espera/Prioridad con
//     scheduled_time vencido y nadie actualizó su estatus).
// Sentado/Cancelado/No Show (5/6/7) ya nunca entraban a
// isOperationalToday/isUpcomingReservation, así que no hace falta
// listarlos aquí aparte.
export function isConcludedOrExpired(item) {
  if (!item) return false;
  if (item.status === 2) return true;
  if (hasReservationTimePassed(item.scheduled_time)) return true;
  return false;
}

// Reserva (status 1) cuya fecha programada todavía no llega → vive en
// la pestaña/vista "Reservaciones".
export function isUpcomingReservation(item) {
  return !!item
    && item.status === 1
    && isFutureReservationDate(item.scheduled_time)
    && !isConcludedOrExpired(item);
}

// Walk-in (Espera/Prioridad, status 3/4) o Reserva cuya fecha ya llegó
// (hoy o antes) o no tiene fecha capturada → vive en la pestaña/vista
// "Lista de Espera de Hoy" (operativa). "Llegó" (2) y lo vencido ya NO
// cuentan como operativo-hoy — ver isConcludedOrExpired.
export function isOperationalToday(item) {
  if (!item) return false;
  if (isConcludedOrExpired(item)) return false;
  if ([3, 4].includes(item.status)) return true;
  if (item.status === 1) return !isFutureReservationDate(item.scheduled_time);
  return false;
}
